import { useEffect, useRef } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import type { Message } from '../types/chat';
import { endConversationSession, saveConversation } from './useConversationStorage';

// Délai en millisecondes avant sauvegarde (5 secondes)
const SAVE_DELAY = 5000;

// Délai d'inactivité avant nouvelle conversation (5 minutes)
const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

export function useConversationTimer(
  messages: Message[], 
  isStreaming: boolean, 
  preview: boolean = false
) {
  const { chatbotPublicId } = useChatbot();
  const timerRef = useRef<NodeJS.Timeout>();
  const inactivityTimerRef = useRef<NodeJS.Timeout>();
  const messagesRef = useRef(messages);
  const isStreamingRef = useRef(isStreaming);
  const lastSavedRef = useRef<string>('');
  const chatbotPublicIdRef = useRef(chatbotPublicId);
  const previewRef = useRef(preview);
  const saveInProgressRef = useRef(false);

  // Garder les références à jour
  useEffect(() => {
    messagesRef.current = messages;
    isStreamingRef.current = isStreaming;
    chatbotPublicIdRef.current = chatbotPublicId;
    previewRef.current = preview;
  }, [messages, isStreaming, chatbotPublicId, preview]);

  // Sauvegarder la conversation quand l'utilisateur quitte la page
  useEffect(() => {
    // Skip effects in preview mode
    if (previewRef.current) return;

    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      if (messagesRef.current.length > 1 && !isStreamingRef.current && !saveInProgressRef.current) {
        // Forcer la sauvegarde synchrone
        saveInProgressRef.current = true;
        try {
          const conversationId = await saveConversation(messagesRef.current, chatbotPublicIdRef.current || undefined);
          if (conversationId) {
            lastSavedRef.current = conversationId;
            endConversationSession();
          }
        } catch (err) {
          console.error('Error saving conversation:', err);
        } finally {
          saveInProgressRef.current = false;
        }
        
        // Message standard pour certains navigateurs
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Timer pour l'inactivité
  useEffect(() => {
    // Skip effects in preview mode
    if (previewRef.current) return;

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      inactivityTimerRef.current = setTimeout(() => {
        endConversationSession();
      }, INACTIVITY_TIMEOUT);
    };

    // Réinitialiser le timer à chaque nouveau message
    if (messages.length > 0) {
      resetInactivityTimer();
    }

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [messages]);

  // Timer normal pour sauvegarder périodiquement
  useEffect(() => {
    // Skip effects in preview mode
    if (previewRef.current) return;

    const saveIfNeeded = async () => {
      // Ne pas sauvegarder si streaming en cours ou pas assez de messages
      if (isStreamingRef.current || messagesRef.current.length <= 1 || saveInProgressRef.current) {
        return;
      }

      saveInProgressRef.current = true;
      try {
        const conversationId = await saveConversation(messagesRef.current, chatbotPublicIdRef.current || undefined);
        if (conversationId) {
          lastSavedRef.current = conversationId;
        }
      } catch (err) {
        console.error('Error saving conversation:', err);
      } finally {
        saveInProgressRef.current = false;
      }
    };

    // Nettoyer le timer précédent
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Démarrer un nouveau timer
    timerRef.current = setTimeout(saveIfNeeded, SAVE_DELAY);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [messages, isStreaming]);
}